// Phase E (ops), gate E2: telemetry on @rpg/telemetry (decision 0064). Off until the player opts in: the setting
// `privacy.telemetry` (default off, in Settings under Privacy) and a one-time ask over the title, which is declined by
// its first, focused button or by back. The event set is the `telemetry-events` table (the kernel events it names, the
// fields it keeps); the numbers are the `telemetry` table's. Error reports: the page's uncaught errors and rejections,
// the boot's captured errors, and the kernel's fail-soft list (a system switched off); each carries the device, the build
// and the last few events. Field samples: frame times from @rpg/perf's field sampler while playing, with the boot's
// load times and the tier.
//
// Backends by configuration (never keys in the code): `VITE_TELEMETRY_BACKENDS` (default `supabase,posthog,sentry`);
// Supabase sends as the signed-in player through the saves' connection (signed out, batches wait); PostHog and Sentry
// are stubs, configured only by `VITE_POSTHOG_KEY` and `VITE_SENTRY_DSN`, which are unset, so they get nothing. Dev
// builds take `?telemetry-backends=memory,…` (the specs' in-memory sink) and `?telemetry-ask=on|off` (the ask is off
// under automation by default, as `?errors=` is fail-fast there, so the suite's title screens are unchanged).
// Planted defect `telemetry-before-opt-in` (`?faults=` or `__rpg.faults`): the consent gate removed.
import { createFieldSampler, glStrings } from "@rpg/perf";
import {
  coarseDevice,
  createTelemetry,
  failSoftWatcher,
  listenForErrors,
  memoryBackend,
  posthogStub,
  selectBackends,
  sentryStub,
  supabaseBackend,
  TELEMETRY_FAULTS,
  type ErrorTarget,
  type EventRule,
  type MemoryBackend,
  type StubBackend,
  type SupabaseLike,
  type Telemetry,
  type TelemetryBackend,
} from "@rpg/telemetry";
import type { EventBus } from "@rpg/kernel";
import type { Runtime } from "@rpg/runtime";
import { Button, h, Menu, Screen, type ScreenRender, type Settings, type Ui } from "@rpg/ui";
import type { WebGLRenderer } from "three";
import type { SpikeBoot } from "../boot";
import type { SpikeContent } from "../content";
import { EVENT_NAMES, type GameEvents } from "../core";
import { HERO_ID } from "../enemies/fight";
import type { FaultGroup } from "../faults";
import type { SpikeSaves } from "../save";

/** The setting that holds the opt-in. */
export const OPT_IN_SETTING = "privacy.telemetry";
/** Where this browser remembers that the player was asked (the ask is per device; the opt-in syncs with the settings). */
export const ASKED_KEY = "green-valley-telemetry-asked";
/** The ask's screen id. */
export const ASK_SCREEN = "telemetry-ask";
/** This module's planted-defect names. */
export const TELEMETRY_FAULT_NAMES = ["telemetry-before-opt-in"] as const;
/** The build every record carries. */
export const BUILD = `0.1.0+${(import.meta.env as Record<string, string | undefined>).VITE_BUILD_ID ?? import.meta.env.MODE}`;

/** What the module needs. */
export interface TelemetryDeps {
  runtime: Runtime;
  renderer: WebGLRenderer;
  content: SpikeContent;
  events: EventBus<GameEvents>;
  boot: SpikeBoot;
  saves: SpikeSaves;
  settings: Settings;
  /** Settings loaded from the save store (the ask waits for them: a synced opt-in needs no ask). */
  settingsReady: () => Promise<unknown>;
  ui: Ui;
  addScreens: (s: Record<string, ScreenRender>) => void;
  /** The kernel's systems (the fail-soft list). */
  systems: () => readonly { name: string; module: string; errors: number; disabled: boolean; firstError?: string }[];
  /** The tier in force. */
  tier: () => string;
  search?: string;
}

const storage = {
  get(k: string): string | null {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set(k: string, v: string): void {
    try {
      localStorage.setItem(k, v);
    } catch {
      /* private mode: the ask comes back next time */
    }
  },
};

/** Mount telemetry. */
export function mountTelemetry(d: TelemetryDeps) {
  const q = new URLSearchParams(d.search ?? location.search);
  const reg = d.content.registry;
  const num = (id: string): number => {
    const r = reg.get("telemetry", id);
    if (!r) throw new Error(`no telemetry value ${id}`);
    return r.value;
  };
  TELEMETRY_FAULTS.sendBeforeOptIn = (q.get("faults") ?? "").split(",").includes("telemetry-before-opt-in");

  // the event set: the table's rows (`where` cleaned of the generated type's undefined)
  const rules: EventRule[] = reg.rows("telemetry-events").map((r) => ({
    id: r.id,
    event: r.event,
    name: r.name,
    ...(r.fields ? { fields: r.fields } : {}),
    ...(r.where ? { where: Object.fromEntries(Object.entries(r.where).filter(([, v]) => v !== undefined)) as EventRule["where"] } : {}),
    ...(r.once !== undefined ? { once: r.once } : {}),
    ...(r.throttleS !== undefined ? { throttleS: r.throttleS } : {}),
  }));

  // backends by configuration
  const env = import.meta.env as Record<string, string | undefined>;
  const names = ((import.meta.env.DEV ? q.get("telemetry-backends") : null) ?? env.VITE_TELEMETRY_BACKENDS ?? "supabase,posthog,sentry").split(",").map((s) => s.trim()).filter(Boolean);
  let memory: MemoryBackend | null = null;
  const stubs: Record<string, StubBackend> = {};
  const factories: Record<string, () => TelemetryBackend> = {
    memory: () => (memory = memoryBackend()),
    supabase: () => supabaseBackend({ client: () => d.saves.account.client() as unknown as SupabaseLike | null }),
    posthog: () => (stubs.posthog = posthogStub({ apiKey: env.VITE_POSTHOG_KEY, host: env.VITE_POSTHOG_HOST })),
    sentry: () => (stubs.sentry = sentryStub({ dsn: env.VITE_SENTRY_DSN })),
  };
  const { backends, unknown } = selectBackends(names, factories);
  if (unknown.length) console.warn(`[telemetry] unknown backends ignored: ${unknown.join(", ")}`);

  // the context every record carries: coarse device facts, the build, the tier
  let gpu = "unknown";
  try {
    gpu = glStrings(d.renderer.getContext() as WebGL2RenderingContext).renderer;
  } catch {
    /* no context info */
  }
  const device = (): Record<string, string | number | boolean | null | number[]> => {
    const p = d.boot.report().platform;
    return { kind: p.kind, ...coarseDevice(navigator as Navigator & { deviceMemory?: number }), gpu, dpr: p.devicePixelRatio, viewport: p.viewport.map((v) => Math.round(v / 10) * 10) };
  };
  const optedIn = (): boolean => d.settings.get<boolean>(OPT_IN_SETTING) === true;
  const tel: Telemetry & { sources(): string[] } = createTelemetry({
    backends,
    events: rules,
    derive: { "combat:defeated": (p) => ({ victim: (p as GameEvents["combat:defeated"]).entity === HERO_ID ? "hero" : "enemy" }) },
    optedIn,
    context: () => ({ build: BUILD, device: device(), tier: d.tier() }),
    batch: {
      maxSize: num("batch.maxSize"),
      maxAgeMs: num("batch.maxAge") * 1000,
      maxQueue: num("batch.maxQueue"),
      attempts: num("retry.attempts"),
      retryBaseMs: num("retry.base") * 1000,
      retryMaxMs: num("retry.max") * 1000,
    },
    recent: num("recent.events"),
  });

  // kernel events the event set names (listening only: the sim never sees telemetry)
  const kernelNames = new Set<string>(EVENT_NAMES);
  for (const name of tel.sources()) {
    if (!kernelNames.has(name)) continue;
    d.events.on(name as keyof GameEvents, (p: unknown) => tel.event(name, p));
  }
  // settings changes, one event per setting after it settles (a slider dragged is one change); the opt-in itself
  const pending = new Map<string, ReturnType<typeof setTimeout>>();
  d.settings.subscribe((ids) => {
    if (ids.includes(OPT_IN_SETTING)) tel.consentChanged();
    for (const id of ids) {
      clearTimeout(pending.get(id));
      pending.set(
        id,
        setTimeout(() => {
          pending.delete(id);
          tel.event("settings:changed", { id, value: d.settings.get(id) });
        }, 1000),
      );
    }
  });

  // errors: the boot's captured errors so far, then the page's uncaught errors, and the kernel's fail-soft list
  for (const e of d.boot.errors) tel.error({ source: "boot", message: e.split("\n")[0] ?? e, stack: e });
  listenForErrors(window as unknown as ErrorTarget, (e) => tel.error(e));
  const failSoft = failSoftWatcher(d.systems, (e) => tel.error(e));

  // field samples: frame times while playing, one sample per `sample.every` seconds of play
  const budget = (): number => (d.tier() === "low" ? num("sample.budget.low") : num("sample.budget.high"));
  let sampler = createFieldSampler({ capacity: num("sample.frames"), budgetMs: budget() });
  let sampleBudget = budget();
  let played = 0;
  let sincePoll = 0;
  const loadMs = (): Record<string, number | null> => {
    const r = d.boot.report();
    return { boot: r.totalMs, ready: r.readyAtMs, firstFrame: r.firstFrameAtMs, rapier: r.rapierMs, recast: r.recastMs };
  };
  const sampleNow = (): boolean => {
    const s = sampler.take();
    if (!s) return false;
    tel.sample({ frameMs: s.frameMs, frames: s.frames, slowFrames: s.slowFrames, loadMs: loadMs(), extra: { budgetMs: s.budgetMs } });
    return true;
  };
  d.runtime.on(
    "render",
    (c) => {
      sincePoll += c.realDt;
      if (sincePoll >= num("errors.poll")) {
        sincePoll = 0;
        failSoft.check();
      }
      if (d.runtime.paused || !optedIn()) return;
      if (budget() !== sampleBudget) {
        sampleNow();
        sampleBudget = budget();
        sampler = createFieldSampler({ capacity: num("sample.frames"), budgetMs: sampleBudget });
      }
      sampler.frame(c.realDt * 1000);
      played += c.realDt;
      if (played >= num("sample.every")) {
        played = 0;
        sampleNow();
      }
    },
    { name: "telemetry: field sampler", order: 900 },
  );
  // leaving the page: what was sampled, then a flush
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "hidden" || !optedIn()) return;
    sampleNow();
    void tel.flush();
  });

  // the one-time ask over the title
  const askPolicy = q.get("telemetry-ask");
  const askOn = askPolicy === "on" || (askPolicy !== "off" && !(typeof navigator !== "undefined" && navigator.webdriver));
  const answer = (on: boolean): void => {
    storage.set(ASKED_KEY, on ? "yes" : "no");
    if (d.ui.top() === ASK_SCREEN) d.ui.pop();
    if (on !== optedIn()) d.settings.set(OPT_IN_SETTING, on);
  };
  d.addScreens({
    [ASK_SCREEN]: (ctx) =>
      h(
        Screen,
        { ctx, title: ctx.t("telemetry.ask.title"), dim: true, onBack: () => answer(false) },
        h("p", { id: "telemetry-ask-body" }, ctx.t("telemetry.ask.body")),
        h(
          Menu,
          null,
          h(Button, { label: ctx.t("telemetry.ask.decline"), variant: "primary", autofocus: true, describedBy: "telemetry-ask-body", action: "telemetry-decline", onPress: () => answer(false) }),
          h(Button, { label: ctx.t("telemetry.ask.accept"), describedBy: "telemetry-ask-body", action: "telemetry-accept", onPress: () => answer(true) }),
        ),
      ),
  });
  const needsAsk = (): boolean => storage.get(ASKED_KEY) === null && !optedIn();
  /** Show the ask over the title once the settings are loaded and the title shows (it reads the slots first). */
  const ask = async (force = false): Promise<boolean> => {
    await d.settingsReady();
    if (!force && !needsAsk()) return false;
    for (let i = 0; i < 300 && d.ui.top() !== "title"; i++) await new Promise((r) => setTimeout(r, 20));
    if (d.ui.top() !== "title") return false;
    d.ui.push(ASK_SCREEN);
    return true;
  };
  if (askOn) d.boot.flow.on((e) => void (e.to === "title" && ask()));

  const faultGroup: FaultGroup = {
    names: TELEMETRY_FAULT_NAMES,
    get: () => TELEMETRY_FAULTS.sendBeforeOptIn,
    set: (_n, on) => void (TELEMETRY_FAULTS.sendBeforeOptIn = on),
  };

  /** The handle's `telemetry` key (dev builds). */
  const handle = {
    stats: () => tel.stats(),
    /** What the in-memory backend received (null without it: `?telemetry-backends=memory,…`). */
    records: () => memory?.records() ?? null,
    /** What a configured stub shaped (empty when unconfigured: nothing reached it). */
    shaped: (name: string) => stubs[name]?.shaped() ?? null,
    backends: () => backends.map((b) => ({ name: b.name, configured: b.configured, accepts: [...b.accepts] })),
    optedIn,
    /** Opt in or out through the setting (as the settings screen does). */
    optIn: (on: boolean) => d.settings.set(OPT_IN_SETTING, on),
    asked: () => storage.get(ASKED_KEY),
    /** Show the ask over the title now (whether or not it was asked before). */
    ask: () => ask(true),
    flush: () => tel.flush().then(() => true),
    sampleNow,
    /** Report an error as the game would (source "manual"). */
    error: (message: string) => tel.error({ source: "manual", message }),
    recent: () => tel.recent(),
    rules: () => rules.map((r) => r.id),
  };
  return { telemetry: tel, handle, faultGroup };
}
